import React, { useRef } from 'react';
import moment from 'moment';
import { useFocusEffect } from '@react-navigation/native';

import { localTime } from '@helpers';
import { scale } from '@components/frame';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { MachinesScreen, TimePeriod } from '@v2/pages/machines/common/enums';
import { getValuesLine, getLineLegend } from '@components/machines/components/eventsChart/calculation';
import { getDateRangeForPeriod } from '@components/machines/components/eventsChart/helpers/getDateRangeForPeriod';
import { DateRangeResult, UseManageLineChartParams } from '@components/machines/components/eventsChart/types';
import { buildMachineStatsGraphParams } from '@reducers/statistics/helpers/buildStatisticsParams';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';

const splitDataByBoundary = (dataFull: any[], endPrev: string, dateField: 'dateTime' | 'date') => {
  let index = 0;
  dataFull.some((e, i) => {
    index = i;
    return localTime(e[dateField]).isAfter(endPrev);
  });
  return {
    dataPrev: dataFull.slice(0, index),
    data: dataFull.slice(index),
  };
};

const getMachineLineCalculationConfig = (
  selectedTime: TimePeriod,
  dateRange: DateRangeResult
): Pick<DateRangeResult, 'amount' | 'amountPrev' | 'period'> => {
  switch (selectedTime) {
    case TimePeriod.WEEK:
      return { amount: 7, amountPrev: 7, period: 'day' };
    case TimePeriod.YEAR:
      return { amount: 12, amountPrev: 12, period: 'month' };
    default:
      return {
        amount: dateRange.amount,
        amountPrev: dateRange.amountPrev,
        period: dateRange.period,
      };
  }
};

export const useManageLineChart = (params: UseManageLineChartParams) => {
  const {
    C,
    fullWidth,
    data,
    dataPrev,
    isActive,
    id,
    selectedChartDate,
    setDataPrev,
    setData,
    setNcFiles,
    getStatsDay,
    getStatsHour,
    getStatsWeek,
    getStats,
    sendLog,
    allSelected,
    selectedTimeCounter,
    chartTitle,
    isMachinesScope,
  } = params;

  const abortControllerLine = useRef<AbortController>();
  const { timePeriod: selectedTime } = useHandleParams();

  useFocusEffect(
    React.useCallback(() => {
      if ((id == null || id === '') && !isMachinesScope) return;
      if (abortControllerLine.current) {
        abortControllerLine.current.abort();
      }

      abortControllerLine.current = new AbortController();
      const signal = abortControllerLine.current.signal;

      const currentSelectedChartDate = selectedChartDate;
      const currentSelectedTime = selectedTime;
      const currentId = id;

      let isEffectActive = true;

      const isRequestStillValid = () => {
        return (
          isEffectActive &&
          !signal.aborted &&
          currentSelectedChartDate === selectedChartDate &&
          currentSelectedTime === selectedTime &&
          currentId === id
        );
      };

      (async () => {
        try {
          const dateRange = getDateRangeForPeriod(currentSelectedTime, currentSelectedChartDate);
          if (!dateRange) return;

          if (!isRequestStillValid()) return;

          if (data?.selectedTime && data.selectedTime !== currentSelectedTime) {
            setData({});
          }
          setDataPrev({});
          setNcFiles([]);
          const performanceStorage: Record<string, any> = {};

          const getRangeStatsByPeriod = () => {
            const statsMap = {
              hour: getStatsHour,
              day: getStatsDay,
              month: undefined,
              isoWeek: getStatsWeek,
            };
            return statsMap[dateRange.period];
          };

          let dataCurrent: any[];
          let dataPrevResult: any[];
          const usesAggregatedStats = isMachinesScope && !!getStats;

          if (usesAggregatedStats) {
            const t1 = performance.now();
            const [currentRes, prevRes] = await Promise.all([
              getStats(buildMachineStatsGraphParams(currentSelectedTime, dateRange.start, currentId, allSelected), signal),
              getStats(buildMachineStatsGraphParams(currentSelectedTime, dateRange.startPrev, currentId, allSelected), signal),
            ]);
            const t2 = performance.now();
            performanceStorage.firstAPIcall = t2 - t1;
            dataCurrent = currentRes || [];
            dataPrevResult = prevRes || [];
          } else {
            // NC-file/clamping stats still use start/end range requests and the existing wide-window split.
            const getStatsByRange = getRangeStatsByPeriod();
            if (!getStatsByRange) return;

            if (dateRange.usesSingleApiCall) {
              const t1 = performance.now();
              const dataFull = (await getStatsByRange({ id: currentId, start: dateRange.startPrev, end: dateRange.end }, signal)) || [];
              const t2 = performance.now();
              performanceStorage.firstAPIcall = t2 - t1;

              if (!isRequestStillValid()) return;

              const dateField = dateRange.period === 'hour' ? 'dateTime' : 'date';
              const split = splitDataByBoundary(dataFull, dateRange.endPrev, dateField);
              dataCurrent = split.data;
              dataPrevResult = split.dataPrev;
            } else {
              const t1 = performance.now();
              const [currentRes, prevRes] = await Promise.all([
                getStatsByRange({ id: currentId, start: dateRange.start, end: dateRange.end }, signal),
                getStatsByRange({ id: currentId, start: dateRange.startPrev, end: dateRange.endPrev }, signal),
              ]);
              const t2 = performance.now();
              performanceStorage.firstAPIcall = t2 - t1;
              dataCurrent = currentRes || [];
              dataPrevResult = prevRes || [];
            }
          }

          performanceStorage.firstAmount = dataCurrent.length;
          performanceStorage.secondAmount = dataPrevResult.length;

          if (!isRequestStillValid()) return;

          const calculationConfig = usesAggregatedStats ? getMachineLineCalculationConfig(currentSelectedTime, dateRange) : dateRange;

          let t1 = performance.now();
          const info = getValuesLine({
            start: dateRange.start,
            data: dataCurrent,
            amount: calculationConfig.amount,
            period: calculationConfig.period,
          });
          let t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;

          t1 = performance.now();
          const infoPrev = getValuesLine({
            start: dateRange.startPrev,
            data: dataPrevResult,
            amount: calculationConfig.amountPrev,
            period: calculationConfig.period,
          });
          t2 = performance.now();
          performanceStorage.secondCalculation = t2 - t1;

          if (!isRequestStillValid()) return;

          setDataPrev({
            ...infoPrev,
            selectedTime: currentSelectedTime,
            start: dateRange.startPrev,
            end: dateRange.endPrev,
          });

          setData({
            ...info,
            selectedTime: currentSelectedTime,
            start: dateRange.start,
            end: dateRange.end,
          });

          if (isRequestStillValid()) {
            sendLog({ title: LOGGER_TITLES.LINE_CHART_PERFORMANCE, data: { ...performanceStorage, selectedTime: currentSelectedTime, machine: currentId }, status: 'debug' });
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Error in chart data loading:', error);
          }
        }
      })();

      return () => {
        isEffectActive = false;
        if (abortControllerLine.current) {
          abortControllerLine.current.abort();
        }
      };
    }, [selectedTime, id, selectedChartDate, selectedTimeCounter, isMachinesScope])
  );

  const displayDataNow = (isActive ? data?.dataLineActive : data?.dataLineProductive) || [];
  const displayDataOld = (isActive ? dataPrev?.dataLineActive : dataPrev?.dataLineProductive) || [];

  const availableWidth = fullWidth - scale(30);
  const isLoading = !displayDataNow.length || !displayDataOld.length || !data?.selectedTime;

  const showFullInfo = !isLoading && moment.utc().isAfter(moment.utc(data?.end));

  const legend = !isLoading
    ? getLineLegend({
        selectedTime: data.selectedTime,
        start: data.start,
        end: data.end,
        showFullInfo,
      })
    : null;

  const offset = scale(6);

  const color: Record<string, string> = {
    [MachinesScreen.ACTIVE]: C('Status/Light/Active'),
    [MachinesScreen.PRODUCTIVE]: C('Status/Light/Productive'),
  };

  const dataFormatted = !isLoading
    ? [
        {
          data: displayDataOld,
          svg: { stroke: C('Gray/Light/3'), strokeWidth: scale(3) },
          showFullLine: true,
        },
        {
          data: displayDataNow,
          svg: { stroke: color[chartTitle], strokeWidth: scale(3) },
          showFullLine: false,
        },
      ]
    : [];

  const chartWidth = availableWidth - 2 * offset;

  return {
    availableWidth,
    isLoading,
    showFullInfo,
    legend,
    offset,
    dataFormatted,
    chartWidth,
    displayDataNow,
    displayDataOld,
  };
};
